// src/components/FilingStatusModal.js
import React, { useState, useRef } from 'react';
import { User, Users, UserCheck, UserMinus, Heart, Upload, AlertCircle, FileText, Trash2 } from 'react-feather';

const STATUS_OPTIONS = [
  { id: 'Single', label: 'Single', description: 'For unmarried individuals.', icon: User },
  { id: 'Married Filing Jointly', label: 'Married Filing Jointly', description: 'For married couples filing one return.', icon: Users },
  { id: 'Married Filing Separately', label: 'Married Filing Separately', description: 'For married couples filing separate returns.', icon: UserMinus },
  { id: 'Head of Household', label: 'Head of Household', description: 'For unmarried individuals with dependents.', icon: UserCheck },
  { id: 'Qualifying Surviving Spouse', label: 'Qualifying Surviving Spouse', description: 'For recent widows/widowers with a dependent child.', icon: Heart },
];

function FilingStatusModal({ formData = {}, onStatusSelect, onClose, onDone, onDataChange }) {
  const { 
    filingStatus = '', 
    spouseFirstName = '',
    spouseMiddleName = '',
    spouseLastName = '',
    qualifyingPersonName = '', 
    nonresidentAlienSpouse = false, 
    nonresidentAlienSpouseName = '', 
    nonresidentAlienStatement = null 
  } = formData;
  
  const [currentView, setCurrentView] = useState('selection');
  const [isAnimating, setIsAnimating] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const statusNeedsInput = (status) => {
    return status === 'Married Filing Separately' ||
           status === 'Head of Household' ||
           status === 'Qualifying Surviving Spouse';
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const syntheticEvent = {
        target: {
          name: 'nonresidentAlienStatement',
          // CHANGED: Store the actual File object, not just metadata
          value: file 
        }
      };
      onDataChange(syntheticEvent);
    }
  };

  const handleRemoveFile = () => {
    const syntheticEvent = {
      target: {
        name: 'nonresidentAlienStatement',
        value: null
      }
    };
    onDataChange(syntheticEvent);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleStatusClick = (statusId) => {
    setError('');
    onStatusSelect(statusId);
    
    if (statusNeedsInput(statusId) || statusId === 'Married Filing Jointly') {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentView('inputs');
        setIsAnimating(false);
      }, 400);
    } else {
      // For Single, we are done immediately
      setTimeout(() => {
        // --- FIX: Pass statusId here ---
        if (onDone) onDone(statusId); 
        else onClose();
      }, 150);
    }
  };

  const handleConfirm = () => {
    // 1. Validate Married Filing Separately
    if (filingStatus === 'Married Filing Separately') {
      if (!spouseFirstName?.trim() || !spouseLastName?.trim()) {
        setError('Please enter your spouse\'s first and last name');
        return;
      }
    }

    // 2. Validate Head of Household / Qualifying Surviving Spouse
    if ((filingStatus === 'Head of Household' || filingStatus === 'Qualifying Surviving Spouse') && !qualifyingPersonName?.trim()) {
      setError('Please enter the qualifying person\'s name');
      return;
    }

    // 3. Validate Nonresident Alien Spouse (MFJ only)
    if (filingStatus === 'Married Filing Jointly' && nonresidentAlienSpouse) {
      if (!nonresidentAlienSpouseName?.trim()) {
        setError('Please enter your nonresident alien spouse\'s name');
        return;
      }
      if (!nonresidentAlienStatement) {
        setError('Please attach the required statement');
        return;
      }
    }
    
    if (onDone) onDone(filingStatus); 
    else onClose();
  };

  const handleBackdropClick = () => {
    if (currentView === 'selection') {
      onClose(); 
    } else {
      handleConfirm(); // This calls onDone(filingStatus) now
    }
  };

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div
        className={`modal-content filing-status-modal ${isAnimating ? 'is-transitioning' : ''}`}
        onClick={e => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>{currentView === 'inputs' ? "Additional Information" : "Select Your Filing Status"}</h2>
          <p>{currentView === 'inputs' ? "This information is required for your selected status." : "This affects your deductions and tax brackets."}</p>
        </div>

        <div className={`modal-body ${currentView === 'inputs' ? 'show-inputs' : 'show-options'}`}>
          
          {/* --- VIEW 1: STATUS SELECTION GRID --- */}
          <div className={`status-options-container ${currentView === 'inputs' ? 'hidden' : ''}`}>
            {STATUS_OPTIONS.map(option => {
              const Icon = option.icon;
              const isSelected = filingStatus === option.id;
              return (
                <button
                  key={option.id}
                  className={`status-option ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleStatusClick(option.id)}
                  style={{
                    borderColor: isSelected ? 'var(--accent-primary)' : 'var(--border-color)',
                    backgroundColor: isSelected ? '#fff7ed' : 'transparent', // Orange tint
                    boxShadow: isSelected ? '0 0 0 2px rgba(235, 130, 88, 0.2)' : 'none'
                  }}
                >
                  <Icon size={28} color={isSelected ? '#EB8258' : '#8b7964ff'} />
                  <h3 style={{ color: isSelected ? '#9a3412' : 'var(--text-primary)' }}>{option.label}</h3>
                  <p>{option.description}</p>
                </button>
              );
            })}
          </div>

          {/* --- VIEW 2: CONDITIONAL INPUTS --- */}
          <div className={`conditional-input-container ${currentView === 'inputs' ? 'visible' : ''}`}>
            {error && <div className="error-message">{error}</div>}
            
            {/* --- Married Filing Separately: Split Name Inputs --- */}
            {filingStatus === 'Married Filing Separately' && (
              <div className="form-group">
                <label>Spouse's Name</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input 
                    type="text" 
                    name="spouseFirstName" 
                    value={spouseFirstName || ''} 
                    onChange={onDataChange}
                    placeholder="First"
                    style={{ flex: 2 }}
                  />
                  <input 
                    type="text" 
                    name="spouseMiddleName" 
                    value={spouseMiddleName || ''} 
                    onChange={onDataChange}
                    placeholder="Middle"
                    style={{ flex: 1 }}
                  />
                  <input 
                    type="text" 
                    name="spouseLastName" 
                    value={spouseLastName || ''} 
                    onChange={onDataChange}
                    placeholder="Last"
                    style={{ flex: 2 }}
                  />
                </div>
                <small style={{ display: 'block', marginTop: '0.5rem', color: 'var(--text-secondary)' }}>
                  Enter your spouse's full legal name.
                </small>
              </div>
            )}

            {/* --- Head of Household / QSS: Qualifying Person --- */}
            {(filingStatus === 'Head of Household' || filingStatus === 'Qualifying Surviving Spouse') && (
              <div className="form-group">
                <label>Qualifying Person's Name</label>
                <input 
                  type="text" 
                  name="qualifyingPersonName" 
                  value={qualifyingPersonName || ''} 
                  onChange={onDataChange}
                  placeholder="Enter qualifying person's name"
                />
                <small style={{ display: 'block', marginTop: '0.5rem', color: 'var(--text-secondary)' }}>
                  Enter the child or relative who qualifies you for this status.
                </small>
              </div>
            )}
            
            {/* --- Married Filing Jointly: Nonresident Alien Election (REWORKED) --- */}
            {filingStatus === 'Married Filing Jointly' && (
              <div style={{ backgroundColor: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '0.5rem', padding: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '1rem' }}>
                    <AlertCircle size={20} className="icon-orange" style={{ marginTop: '2px' }} />
                    <div>
                        <h4 style={{ margin: '0 0 0.25rem 0', color: '#9a3412', fontSize: '1rem' }}>Nonresident Alien Spouse Election</h4>
                        <p style={{ margin: 0, fontSize: '0.9rem', color: '#c2410c', lineHeight: '1.4' }}>
                            Is your spouse a nonresident alien? You can elect to treat them as a U.S. resident for tax purposes to file jointly.
                        </p>
                    </div>
                </div>

                <div className="radio-group" style={{ marginBottom: '1rem' }}>
                    <input 
                        type="radio" 
                        id="nra_no" 
                        name="nonresidentAlienSpouse" 
                        checked={!nonresidentAlienSpouse}
                        onChange={() => {
                            // FIX: Explicitly pass checked: false to update boolean state
                            const syntheticEvent = { target: { name: 'nonresidentAlienSpouse', type: 'checkbox', checked: false } };
                            onDataChange(syntheticEvent);
                        }}
                    />
                    <label htmlFor="nra_no">No</label>

                    <input 
                        type="radio" 
                        id="nra_yes" 
                        name="nonresidentAlienSpouse" 
                        checked={nonresidentAlienSpouse}
                        onChange={() => {
                            // FIX: Explicitly pass checked: true to update boolean state
                            const syntheticEvent = { target: { name: 'nonresidentAlienSpouse', type: 'checkbox', checked: true } };
                            onDataChange(syntheticEvent);
                        }}
                    />
                    <label htmlFor="nra_yes">Yes</label>
                </div>
                
                {/* Only show these if Yes is selected */}
                {nonresidentAlienSpouse && (
                  <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px dashed #fed7aa', animation: 'fadeIn 0.3s' }}>
                    <div className="form-group">
                      <label style={{ color: '#9a3412' }}>Nonresident Alien Spouse's Name</label>
                      <input 
                        type="text" 
                        name="nonresidentAlienSpouseName" 
                        value={nonresidentAlienSpouseName || ''} 
                        onChange={onDataChange}
                        placeholder="Enter spouse's full name"
                        style={{ borderColor: '#fed7aa' }}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label style={{ color: '#9a3412' }}>Attach Election Statement</label>
                      <input 
                        ref={fileInputRef}
                        type="file" 
                        id="nra-file-upload"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                      />
                      
                      {!nonresidentAlienStatement ? (
                          <button 
                            type="button"
                            className="simple-upload-button"
                            onClick={() => fileInputRef.current?.click()}
                            style={{ 
                                borderColor: '#fed7aa', 
                                color: '#9a3412', 
                                backgroundColor: '#ffffff',
                                width: '100%',
                                justifyContent: 'center'
                            }}
                          >
                            <Upload size={18} />
                            Upload Signed Statement
                          </button>
                      ) : (
                          <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'space-between', 
                              padding: '0.75rem', 
                              backgroundColor: '#ffffff', 
                              border: '1px solid #fed7aa', 
                              borderRadius: '0.375rem' 
                          }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', overflow: 'hidden' }}>
                                  <FileText size={18} className="icon-orange" />
                                  <span style={{ fontSize: '0.9rem', color: '#554333ff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {nonresidentAlienStatement.name}
                                  </span>
                              </div>
                              <button 
                                type="button"
                                onClick={handleRemoveFile}
                                style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px' }}
                                title="Remove File"
                              >
                                <Trash2 size={16} />
                              </button>
                          </div>
                      )}
                      
                      <div style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: '#c2410c', lineHeight: '1.4' }}>
                        <strong>Required:</strong> Attach a statement signed by both spouses electing to treat the nonresident alien spouse as a U.S. resident.
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <button onClick={handleConfirm} className="modal-confirm-button" style={{marginTop: '1.5rem', width: '100%'}}>
              Confirm and Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FilingStatusModal;